import { type SessionEnd, sessionEnd } from '@/features/auth/session';
import type { ProblemDetails } from '@/lib/api';

/*
  The idle limit (ALS-494). A session ends after the server's Auth.SessionTimeoutMinutes
  without the person at the screen, and this is the browser's half of that: it counts typing,
  clicking, scrolling and touch as the person being there, tells the server so with a light
  keep-alive while they work, warns a minute before the limit and signs out when it arrives.

  How the two clocks agree. The server counts from the last keep-alive it received (the
  session's active_at claim) and nothing else, so a page polling in a forgotten tab does not
  keep it alive. The browser counts from the last activity. A keep-alive goes whenever there
  has been activity since the last one and the last one is at least the interval old (two
  minutes, or a quarter of a limit short enough to need it), checked on every activity and
  every tick, so every activity reaches the server within the interval and always after it
  happened. The interval being well inside the limit, the report lands while the server's
  previous limit is still running: the server's limit therefore falls at or after the
  browser's, never before, and the browser is the one that signs out on time. Each side reads
  only its own clock, so a laptop whose clock is minutes out makes no difference. If the
  browser is closed or asleep instead, the server ends the session by itself at most one
  interval after the browser would have.

  Several tabs share one clock through localStorage: the last activity and the last keep-alive
  are written there and read on every tick, so activity in any tab keeps every tab alive, the
  warning shows and clears in all of them together, and a sign-out is announced there for the
  others to follow. Storage can be unavailable or throw (a private window, blocked site data);
  each tab then keeps its own clock in memory and the server stays the authority.
*/

/** The warning comes this long before the limit, and counts down from it. */
export const WARNING_MS = 60_000;

/**
 * The keep-alive goes at most this often while the person works, or every quarter of the limit
 * when that is shorter: the interval has to sit well inside the limit for the clocks to agree.
 */
export const KEEP_ALIVE_EVERY_MS = 2 * 60_000;

/** How often a tab looks at the clock, and so how the countdown steps. */
const TICK_MS = 1_000;

/** Activity is written down at most this often, so a scroll does not write storage per frame. */
const RECORD_EVERY_MS = 1_000;

/**
 * Typing, clicking, scrolling and touch. Listened for on the window in the capture phase, so a
 * scroll inside a page's own scrolling body counts (scroll does not bubble) and a handler that
 * stops propagation cannot hide a click. Moving the mouse alone does not count.
 */
const ACTIVITY_EVENTS = ['keydown', 'pointerdown', 'wheel', 'scroll', 'touchstart'] as const;

const ACTIVE_AT = 'alist-admin:active-at';
const KEPT_ALIVE_AT = 'alist-admin:kept-alive-at';
type ClockKey = typeof ACTIVE_AT | typeof KEPT_ALIVE_AT;
const TIMEOUT_MINUTES = 'alist-admin:session-timeout-minutes';
const SIGNED_OUT = 'alist-admin:signed-out';

/** Why a session ended, as one tab tells the others. */
export const SignOutCause = {
    /** The idle limit ran out: every tab goes to sign-in saying so. */
    Idle: 'idle',
    /** The person signed out: every tab goes to sign-in without a message. */
    Chosen: 'chosen',
} as const;
export type SignOutCause = (typeof SignOutCause)[keyof typeof SignOutCause];

export interface IdleSessionOptions {
    /** The limit, as the keep-alive answered it. */
    timeoutMinutes: number;
    /** Tells the server the person is still there. A refusal is the API client's to handle. */
    keepAlive: () => Promise<unknown>;
    /** Every tick while the warning is up, the time left; null when it comes down. */
    onWarning: (remainingMs: number | null) => void;
    /** The limit ran out in this tab: end the session. */
    onTimedOut: () => void;
    /** Another tab ended the session. */
    onSignedOutElsewhere: (cause: SignOutCause) => void;
    /** Where activity and other tabs' storage events arrive. The window, outside a test. */
    target?: EventTarget;
}

export interface IdleSession {
    /** The warning was answered: the person is here, whichever button they pressed. */
    stay: () => void;
    stop: () => void;
}

/**
 * Started once the keep-alive a page sends as it loads has answered, which is how the limit is
 * known; that load counts as the last activity and that answer as the last keep-alive.
 */
export function startIdleSession({
    timeoutMinutes,
    keepAlive,
    onWarning,
    onTimedOut,
    onSignedOutElsewhere,
    target = window,
}: IdleSessionOptions): IdleSession {
    const timeoutMs = timeoutMinutes * 60_000;
    const keepAliveEveryMs = Math.min(KEEP_ALIVE_EVERY_MS, timeoutMs / 4);
    // This tab's own copy of the shared clock, for when storage is unavailable or lags.
    const memory: Record<ClockKey, number> = { [ACTIVE_AT]: 0, [KEPT_ALIVE_AT]: 0 };
    let stopped = false;
    let warning = false;
    let keeping = false;
    let timer = 0;

    function read(key: ClockKey) {
        return Math.max(memory[key], Number(readShared(key)) || 0);
    }

    function record(key: ClockKey, at: number) {
        memory[key] = at;
        writeShared(key, String(at));
    }

    function lapsed(now: number) {
        return now - read(ACTIVE_AT) >= timeoutMs;
    }

    function keepAliveIfDue(now: number) {
        const keptAliveAt = read(KEPT_ALIVE_AT);
        if (keeping || read(ACTIVE_AT) <= keptAliveAt || now - keptAliveAt < keepAliveEveryMs) {
            return;
        }
        // Claimed before it is sent, so the other tabs do not send one of their own. A failure
        // is not retried until the next activity: a network that is down fails the save too,
        // and a 401 has already sent the page to sign-in through the API client.
        record(KEPT_ALIVE_AT, now);
        keeping = true;
        void keepAlive()
            .catch(() => undefined)
            .finally(() => {
                keeping = false;
            });
    }

    /** The tick. `mayKeepAlive` is off when another tab's write prompted it: that tab sends its own. */
    function check(mayKeepAlive = true) {
        if (stopped) return;
        const now = Date.now();
        if (lapsed(now)) {
            stop();
            onTimedOut();
            return;
        }
        const remaining = timeoutMs - (now - read(ACTIVE_AT));
        const warn = remaining <= WARNING_MS;
        if (warn) onWarning(remaining);
        else if (warning) onWarning(null);
        warning = warn;
        if (mayKeepAlive) keepAliveIfDue(now);
    }

    function onActivity() {
        // The warning wants an answer, not a stray scroll behind it.
        if (stopped || warning) return;
        const now = Date.now();
        // Too late to count: the server has ended the session, so this tab follows it.
        if (lapsed(now)) {
            check();
            return;
        }
        if (now - memory[ACTIVE_AT] < RECORD_EVERY_MS) return;
        record(ACTIVE_AT, now);
        keepAliveIfDue(now);
    }

    function onStorage(event: Event) {
        const { key, newValue } = event as StorageEvent;
        if (key === SIGNED_OUT && newValue && !stopped) {
            stop();
            onSignedOutElsewhere(causeOf(newValue));
        } else if (key === ACTIVE_AT) {
            // Another tab answered the warning: take it down now rather than on the next tick.
            check(false);
        }
    }

    function stop() {
        stopped = true;
        window.clearInterval(timer);
        for (const type of ACTIVITY_EVENTS) target.removeEventListener(type, onActivity, true);
        target.removeEventListener('storage', onStorage);
    }

    const started = Date.now();
    record(ACTIVE_AT, started);
    record(KEPT_ALIVE_AT, started);
    writeShared(TIMEOUT_MINUTES, String(timeoutMinutes));
    for (const type of ACTIVITY_EVENTS) {
        target.addEventListener(type, onActivity, { capture: true, passive: true });
    }
    target.addEventListener('storage', onStorage);
    timer = window.setInterval(() => check(), TICK_MS);

    return {
        stay: () => {
            if (stopped) return;
            // An answer after the limit is too late too, and check() says so.
            if (!lapsed(Date.now())) record(ACTIVE_AT, Date.now());
            check();
        },
        stop,
    };
}

/**
 * Tells every other open tab the session has ended and why. The time makes each announcement
 * a change, so a second sign-out after a sign-in still reaches them.
 */
export function announceSignOut(cause: SignOutCause) {
    writeShared(SIGNED_OUT, JSON.stringify({ cause, at: Date.now() }));
}

/**
 * What a 401 tells the login screen. A tab that never had a session is just logging in. When
 * the API says the account changed (ALS-525) that is the reason, whatever the clock says. A tab
 * that had a session is otherwise told the idle limit ran out when the shared clock says so (a
 * laptop that slept through the warning, a request that beat this tab's own tick to it), and
 * that its session expired when it did not (an account deactivated).
 */
export function signedOutSearch(problem?: ProblemDetails): {
    reason?: SessionEnd;
    minutes?: number;
} {
    const reason = sessionEnd(problem);
    if (reason !== 'expired') return reason ? { reason } : {};
    const minutes = Number(readShared(TIMEOUT_MINUTES)) || 0;
    const activeAt = Number(readShared(ACTIVE_AT)) || 0;
    if (minutes > 0 && activeAt > 0 && Date.now() - activeAt >= minutes * 60_000) {
        return { reason: 'idle', minutes };
    }
    return { reason };
}

function causeOf(announcement: string): SignOutCause {
    try {
        const parsed = JSON.parse(announcement) as { cause?: unknown };
        if (parsed.cause === SignOutCause.Idle) return SignOutCause.Idle;
    } catch {
        // not ours: read it as a plain sign-out
    }
    return SignOutCause.Chosen;
}

function readShared(key: string) {
    try {
        return window.localStorage.getItem(key);
    } catch {
        return null;
    }
}

function writeShared(key: string, value: string) {
    try {
        window.localStorage.setItem(key, value);
    } catch {
        // unavailable: this tab keeps its own clock
    }
}
